import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Lock, ArrowLeft, BarChart3, Award, Users, FileCheck2, Loader2 } from "lucide-react";
import { fetchAdminStats } from "../lib/api";

const KEY_LS = "bscc.admin.key.v1";

export default function Admin() {
    const [params, setParams] = useSearchParams();
    const [key, setKey] = useState(() => params.get("key") || sessionStorage.getItem(KEY_LS) || "");
    const [stats, setStats] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = async (k) => {
        if (!k) return;
        setLoading(true);
        setError(null);
        try {
            const data = await fetchAdminStats(k);
            setStats(data);
            sessionStorage.setItem(KEY_LS, k);
        } catch (e) {
            setError(e?.response?.status === 401 ? "Invalid passphrase" : "Couldn't load stats");
            setStats(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (key) load(key);
    }, [key]);

    const submit = (e) => {
        e.preventDefault();
        const k = e.target.elements.key.value.trim();
        setKey(k);
        setParams({});
    };

    if (!stats) {
        return (
            <div className="min-h-screen flex items-center justify-center px-6 py-16 bg-[#F7F7F5]" data-testid="admin-locked">
                <form onSubmit={submit} className="max-w-md w-full bg-white border border-gray-200 rounded-lg shadow-sm p-8">
                    <div className="h-12 w-12 rounded-full bg-[#2C7A85] grid place-items-center text-white mb-6">
                        <Lock className="h-5 w-5" />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: "Outfit" }}>BSCC Admin</h1>
                    <p className="text-[15px] text-gray-600 mb-6 leading-relaxed">
                        Enter the admin passphrase to view aggregate training and certificate stats.
                    </p>
                    <input
                        type="password"
                        name="key"
                        defaultValue={key}
                        placeholder="Admin passphrase"
                        autoFocus
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-lg focus:ring-2 focus:ring-[#2C7A85] focus:border-transparent outline-none mb-3"
                        data-testid="admin-key-input"
                    />
                    {error && <div className="text-sm text-[#B91C1C] mb-3" data-testid="admin-error">{error}</div>}
                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-[#2C7A85] hover:bg-[#1F5F69] disabled:opacity-50 text-white font-medium py-2.5 rounded-md transition-colors"
                        data-testid="admin-submit"
                    >
                        {loading ? "Checking…" : "Sign in"}
                    </button>
                    <div className="mt-6">
                        <Link to="/" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-[#2C7A85]">
                            <ArrowLeft className="h-4 w-4" /> Back to handbook
                        </Link>
                    </div>
                </form>
            </div>
        );
    }

    const sectionCounts = Object.entries(stats.acks_by_section || {}).sort((a, b) => b[1] - a[1]);
    const maxCount = Math.max(1, ...sectionCounts.map(([, v]) => v));

    return (
        <div className="fade-up" data-testid="admin-page">
            <div className="max-w-6xl mx-auto px-6 lg:px-12 py-10 lg:py-14">
                <div className="flex items-center justify-between mb-8">
                    <Link to="/" className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-[#2C7A85]">
                        <ArrowLeft className="h-4 w-4" /> Back
                    </Link>
                    <button
                        onClick={() => { sessionStorage.removeItem(KEY_LS); setKey(""); setStats(null); }}
                        className="text-sm text-gray-500 hover:text-[#B91C1C]"
                        data-testid="admin-signout"
                    >
                        Sign out
                    </button>
                </div>

                <div className="text-[11px] uppercase tracking-[0.2em] text-[#E57373] font-semibold mb-3">Admin</div>
                <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3" style={{ fontFamily: "Outfit" }}>Training overview</h1>
                <p className="text-lg text-gray-600 mb-10 leading-relaxed">
                    Aggregate acknowledgements and certificates across all devices using the handbook. Data is anonymous unless a volunteer enters their name on a certificate.
                </p>

                {loading && (
                    <div className="text-gray-500 mb-6 inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Refreshing…</div>
                )}

                {/* KPI tiles */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
                    <StatTile icon={FileCheck2} label="Acknowledgements" value={stats.total_acks} accent="#2C7A85" />
                    <StatTile icon={Users} label="Devices active" value={stats.unique_devices} accent="#E57373" />
                    <StatTile icon={Award} label="Certificates issued" value={stats.total_certificates} accent="#1F5F69" />
                </div>

                {/* Section breakdown */}
                <div className="bg-white border border-gray-200 rounded-lg p-6 sm:p-8 mb-8">
                    <div className="flex items-center gap-2 mb-5">
                        <BarChart3 className="h-5 w-5 text-[#2C7A85]" />
                        <h2 className="text-xl font-semibold text-gray-900" style={{ fontFamily: "Outfit" }}>Acknowledgements by section</h2>
                    </div>
                    {sectionCounts.length === 0 ? (
                        <div className="text-gray-500 text-sm">No acknowledgements yet.</div>
                    ) : (
                        <ul className="space-y-3">
                            {sectionCounts.map(([sid, count]) => (
                                <li key={sid} data-testid={`section-stat-${sid}`}>
                                    <div className="flex items-baseline justify-between mb-1.5">
                                        <div className="text-[15px] text-gray-900 capitalize">{sid.replace(/-/g, " ")}</div>
                                        <div className="text-sm text-gray-500 tabular-nums">{count}</div>
                                    </div>
                                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-[#2C7A85]"
                                            style={{ width: `${(count / maxCount) * 100}%` }}
                                        />
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Recent certificates */}
                <div className="bg-white border border-gray-200 rounded-lg p-6 sm:p-8">
                    <h2 className="text-xl font-semibold text-gray-900 mb-5" style={{ fontFamily: "Outfit" }}>Recent certificates</h2>
                    {(!stats.recent_certificates || stats.recent_certificates.length === 0) ? (
                        <div className="text-gray-500 text-sm">No certificates issued yet.</div>
                    ) : (
                        <div className="divide-y divide-gray-100">
                            {stats.recent_certificates.map((c, i) => (
                                <div key={i} className="flex items-center justify-between py-3" data-testid={`cert-row-${i}`}>
                                    <div>
                                        <div className="font-medium text-gray-900">{c.volunteer_name || "(unnamed)"}</div>
                                        <div className="text-sm text-gray-500">{c.section_title} · {c.count} items</div>
                                    </div>
                                    <div className="text-sm text-gray-500 tabular-nums">
                                        {new Date(c.timestamp).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function StatTile({ icon: Icon, label, value, accent }) {
    return (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center gap-2 mb-3">
                <Icon className="h-4 w-4" style={{ color: accent }} />
                <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">{label}</div>
            </div>
            <div className="text-4xl font-bold text-gray-900 tabular-nums" style={{ fontFamily: "Outfit" }}>{value}</div>
        </div>
    );
}
